import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleMinus,
  Info,
} from 'lucide-react';
import {
  FormFieldConfig,
  Mold,
  MoldOrderFirstLevelType,
  MoldOrderType,
  OrderDetail,
  requireStorageLocation,
} from '../../types/mold';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './CustomTabs';
import StickyBottomBar from './StickyBottomBar';
import FormFieldWrapper from '../shared/FormFieldWrapper';
import Modal from '../shared/Modal';
import { useAppState } from '../../context/AppStateContext';
import { isFieldRequired } from '@/utils/formUtils';
import { getBangkokDateWithOffset } from '@/utils/dateUtils';

const isDefaultableValueEmpty = (value: unknown) =>
  value === undefined || value === null || value === '';

const hasFieldValueToClear = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const applyDefaultValuesFromMold = (
  details: OrderDetail,
  formFields: FormFieldConfig[],
  mold: Mold,
): OrderDetail => {
  let nextDetails = details;

  formFields.forEach((field) => {
    if (!field.defaultValueFromMold || (field.showIf && !field.showIf(nextDetails))) {
      return;
    }

    const currentValue = nextDetails[field.key as keyof OrderDetail];
    if (!isDefaultableValueEmpty(currentValue)) {
      return;
    }

    const defaultValue = field.defaultValueFromMold(mold);
    if (defaultValue === undefined) {
      return;
    }

    nextDetails = {
      ...nextDetails,
      [field.key]: defaultValue,
    };
  });

  return nextDetails;
};

const applyConfiguredFormDefaults = (
  details: OrderDetail,
  formFields: FormFieldConfig[],
  mold: Mold,
): OrderDetail => {
  let nextDetails: OrderDetail = {
    ...details,
    form: details.form ?? {},
  };

  formFields.forEach((field) => {
    const currentValue = nextDetails[field.key as keyof OrderDetail];
    if (!isDefaultableValueEmpty(currentValue)) return;

    if (
      field.type === 'date' &&
      (field.key === 'dueDate' || field.defaultDateOffset !== undefined)
    ) {
      nextDetails = {
        ...nextDetails,
        [field.key]: getBangkokDateWithOffset(field.defaultDateOffset ?? 0),
      };
      return;
    }

    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      nextDetails = { ...nextDetails, [field.key]: field.defaultValue };
    }
  });

  return applyDefaultValuesFromMold(nextDetails, formFields, mold);
};

const isRequiredValueMissing = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const getMissingOrderFieldLabels = (
  order: OrderDetail,
  formFields: FormFieldConfig[],
): string[] => {
  const missingLabels = formFields
    .filter((field) => {
      if (field.showIf && !field.showIf(order)) return false;
      return (
        isFieldRequired(field, order) &&
        isRequiredValueMissing(order[field.key as keyof OrderDetail])
      );
    })
    .map((field) => field.label);

  if (requireStorageLocation(order.moldOrderType)) {
    if (order.storageLocation === 'other_custom_text') {
      if (!order.customStorageLocation?.trim()) {
        missingLabels.push('ระบุสถานที่เก็บเพิ่มเติม');
      }
    } else if (!order.storageLocation) {
      missingLabels.push('สถานที่เก็บหลังล้าง');
    }
  }

  return [...new Set(missingLabels)];
};

interface MoldFormContentProps {
  mold: Mold;
  details: OrderDetail;
  onDetailsChange: (details: OrderDetail) => void;
  onRemove: () => void;
  disableRemove: boolean;
  parentOrderId?: number | null;
  formFields: FormFieldConfig[];
  lockedFields?: string[];
}

const MoldFormContent: React.FC<MoldFormContentProps> = ({
  mold,
  details,
  onDetailsChange,
  onRemove,
  disableRemove,
  parentOrderId,
  formFields,
  lockedFields = [],
}) => {
  const handleChange = (field: keyof OrderDetail, value: any) => {
    const nextDetails = {
      ...details,
      [field]: value,
    };

    onDetailsChange(
      field === 'moldOrderType'
        ? applyDefaultValuesFromMold(nextDetails, formFields, mold)
        : nextDetails,
    );
  };

  useEffect(() => {
    let nextDetails = details;
    let hasChanges = false;

    formFields.forEach((field) => {
      const fieldKey = field.key as keyof OrderDetail;
      if (field.showIf && !field.showIf(details) && hasFieldValueToClear(details[fieldKey])) {
        nextDetails = {
          ...nextDetails,
          [fieldKey]: '',
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      onDetailsChange(nextDetails);
    }
  }, [details, formFields, onDetailsChange]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 pb-16">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                แม่พิมพ์ {mold.sbiId}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                ชิ้นส่วนที่เกี่ยวข้อง:{' '}
                {mold.productParts.map((p) => p.partNumber).join(', ')}
              </p>
              {parentOrderId && (
                <p className="mt-4 text-sm text-gray-600">
                  สร้างจากใบงานรหัส #{parentOrderId}
                </p>
              )}
            </div>
            {!disableRemove && (
              <button
                onClick={onRemove}
                className="text-gray-400 hover:text-red-500 focus:outline-none transition-colors duration-200"
              >
                <CircleMinus size={24} />
              </button>
            )}
          </div>

          <div className="space-y-6">
            {formFields.map(
              (field) =>
                (!field.showIf || field.showIf(details)) && (
                  <FormFieldWrapper
                    key={field.key as keyof OrderDetail}
                    config={field}
                    value={details[field.key as keyof OrderDetail]}
                    onChange={(value) => handleChange(field.key as keyof OrderDetail, value)}
                    disabled={lockedFields.includes(field.key as string)}
                    details={details}
                  />
                ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface MoldOrderFormProps {
  molds: Mold[];
  initialOrders?: Record<string, OrderDetail>;
  onNextStep: (orders: Record<string, OrderDetail>) => void;
  onPrevStep: () => void;
  isPreselected: boolean;
  parentOrderId?: number | null;
  moldOrderFirstLevelType: MoldOrderFirstLevelType;
}

const MoldOrderForm: React.FC<MoldOrderFormProps> = ({
  molds,
  initialOrders,
  onNextStep,
  onPrevStep,
  isPreselected,
  parentOrderId,
  moldOrderFirstLevelType,
}) => {
  // Read lockFields from query params for field locking
  const { search } = useLocation();
  const { getFormFields } = useAppState();
  const params = new URLSearchParams(search);
  const lockedFields = params.get('lockFields')?.split(',') || [];
  const [activeTab, setActiveTab] = useState(molds[0]?.id || '');
  const [moldOrders, setMoldOrders] = useState<Record<string, OrderDetail>>({});
  const [invalidFields, setInvalidFields] = useState<Record<string, string[]>>({});
  const formFields = useMemo(
    () => getFormFields(moldOrderFirstLevelType),
    [getFormFields, moldOrderFirstLevelType],
  );

  useEffect(() => {
    if (initialOrders && Object.keys(initialOrders).length > 0) {
      const ordersWithMoldDefaults = { ...initialOrders };
      molds.forEach((mold) => {
        const initialOrder = ordersWithMoldDefaults[mold.id];
        if (initialOrder) {
          ordersWithMoldDefaults[mold.id] = applyConfiguredFormDefaults(
            initialOrder,
            formFields,
            mold,
          );
        }
      });
      setMoldOrders(ordersWithMoldDefaults);
    } else {
      const emptyOrders: Record<string, OrderDetail> = {};
      const isAutoFillOrderType = ['mold_acceptance', 'mold_export'].includes(moldOrderFirstLevelType);
      const moldOrderType = isAutoFillOrderType ? moldOrderFirstLevelType : '' as MoldOrderType;
      molds.forEach((mold) => {
        emptyOrders[mold.id] = applyConfiguredFormDefaults(
          {
            moldOrderType: moldOrderType as MoldOrderType,
            note: '',
            form: {},
          },
          formFields,
          mold,
        );
      });
      setMoldOrders(emptyOrders);
    }
  }, [initialOrders, molds, formFields, moldOrderFirstLevelType]);

  const handleDetailsChange = (moldId: string, details: OrderDetail) => {
    setMoldOrders((prev) => ({
      ...prev,
      [moldId]: details,
    }));
    setInvalidFields((prev) => {
      if (!prev[moldId]) return prev;
      const next = { ...prev };
      delete next[moldId];
      return next;
    });
  };

  const handleRemoveMold = (moldId: string) => {
    // Implement remove logic
    console.log('Removing mold:', moldId);
  };

  const readinessByMold = useMemo(
    () =>
      Object.fromEntries(
        molds.map((mold) => {
          const order = moldOrders[mold.id];
          const missingFields = order
            ? getMissingOrderFieldLabels(order, formFields)
            : formFields
                .filter((field) => isFieldRequired(field, {}))
                .map((field) => field.label);
          return [mold.id, { isReady: !!order && missingFields.length === 0, missingFields }];
        }),
      ) as Record<string, { isReady: boolean; missingFields: string[] }>,
    [formFields, moldOrders, molds],
  );
  const readyCount = molds.filter((mold) => readinessByMold[mold.id]?.isReady).length;
  const activeMoldIndex = Math.max(0, molds.findIndex((mold) => mold.id === activeTab));
  const firstIncompleteMold = molds.find((mold) => !readinessByMold[mold.id]?.isReady);

  const goToMoldIndex = (index: number) => {
    const mold = molds[index];
    if (mold) setActiveTab(mold.id);
  };

  const handleNextStep = () => {
    const invalidFieldsMapping: Record<string, string[]> = {};

    molds.forEach((mold) => {
      const missingFields = readinessByMold[mold.id]?.missingFields ?? [];
      if (missingFields.length > 0) invalidFieldsMapping[mold.id] = missingFields;
    });

    if (Object.keys(invalidFieldsMapping).length === 0) {
      onNextStep(moldOrders);
    } else {
      setInvalidFields(invalidFieldsMapping);
      const firstInvalidMold = molds.find((mold) => invalidFieldsMapping[mold.id]);
      if (firstInvalidMold) setActiveTab(firstInvalidMold.id);
    }
  };

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-20 mb-3 border border-gray-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur sm:rounded-xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-semibold text-gray-900">
                แม่พิมพ์ {activeMoldIndex + 1} จาก {molds.length}
              </span>
              <span
                className={`font-semibold ${readyCount === molds.length ? 'text-green-700' : 'text-amber-700'}`}
                data-testid="batch-ready-count"
              >
                พร้อมสร้าง {readyCount}/{molds.length}
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-green-500 transition-all duration-300"
                style={{ width: `${molds.length > 0 ? (readyCount / molds.length) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 sm:justify-end">
            {firstIncompleteMold && firstIncompleteMold.id !== activeTab && (
              <button
                type="button"
                onClick={() => setActiveTab(firstIncompleteMold.id)}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                ไปยังรายการที่ยังไม่ครบ
              </button>
            )}
            <button
              type="button"
              onClick={() => goToMoldIndex(activeMoldIndex - 1)}
              disabled={activeMoldIndex === 0}
              aria-label="แม่พิมพ์ก่อนหน้า"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> ก่อนหน้า
            </button>
            <button
              type="button"
              onClick={() => goToMoldIndex(activeMoldIndex + 1)}
              disabled={activeMoldIndex >= molds.length - 1}
              aria-label="แม่พิมพ์ถัดไป"
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              ถัดไป <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <Tabs value={activeTab} onChange={setActiveTab} className="w-full">
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto">
            <TabsList>
              {molds.map((mold) => (
                <TabsTrigger
                  key={mold.id}
                  value={mold.id}
                  currentValue={activeTab}
                  onClick={setActiveTab}
                >
                  <span className="inline-flex items-center gap-2">
                    {readinessByMold[mold.id]?.isReady ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="พร้อมสร้าง" />
                    ) : (
                      <CircleAlert className="h-4 w-4 text-amber-500" aria-label="ข้อมูลยังไม่ครบ" />
                    )}
                    แม่พิมพ์ {mold.sbiId}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {molds.map((mold) => (
          <TabsContent key={mold.id} value={mold.id} currentValue={activeTab}>
            <MoldFormContent
              mold={mold}
              details={moldOrders[mold.id] || {}} // Ensure details is defined
              onDetailsChange={(details) =>
                handleDetailsChange(mold.id, details)
              }
              onRemove={() => handleRemoveMold(mold.id)}
              disableRemove={isPreselected}
              parentOrderId={parentOrderId}
              formFields={formFields}
              lockedFields={lockedFields}
            />
          </TabsContent>
        ))}
      </Tabs>

      <StickyBottomBar
        onPrevStep={onPrevStep}
        onNextStep={handleNextStep}
        hidePrev={isPreselected}
      />

      {Object.values(invalidFields).length > 0 && (
        <Modal onClose={() => setInvalidFields({})} isBackgroundTransparent={true}>
          <div className="px-4" data-cy="invalid-fields-modal">
            <Info size={48} className="mx-auto text-blue-500" />
            <h2 className="w-full text-center text-lg font-semibold text-blue-500">
              กรุณากรอกข้อมูลให้ครบถ้วน
            </h2>
            {Object.keys(invalidFields).map((key, index) => (
              <div key={index}>
                <p className="mt-2 text-sm text-gray-600 font-bold">
                  แม่พิมพ์: {molds.find((mold) => mold.id === key)?.sbiId ?? key}
                </p>
                <p className="ml-4 mt-1 text-sm text-gray-600">
                  {invalidFields[key].join(', ')}
                </p>
              </div>
            ))}
            <button
              onClick={() => setInvalidFields({})}
              data-cy="close-invalid-fields-modal"
              className="w-full mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              แก้ไขข้อมูล
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MoldOrderForm;
